import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "c-wrapper" }
const _hoisted_2 = { class: "calendar__body" }
const _hoisted_3 = ["onMouseover", "onMousedown"]

import styled from 'vue-styled-components';
import { i18n } from '@/i18n';
import dayjs from 'dayjs';
import { computed, ComputedRef, onMounted, reactive, ref, watch } from 'vue';

// interface
interface Props {
  organizationId: string;
  month: string | number;
  year: string | number;
  activeClass: string;
  prefixClass: string;
  activeDates?: Array<any>;
  processMonthTitle?: (monthTitle: any, year: string | number) => string;
  lang?: string;
}

interface IMonthCalendar {
  showDays: any[];
  isMouseDown: boolean;
}

type ShowDays = {
  value: string | number;
  active: boolean;
  isOtherMonth: boolean;
  className: any;
};

type ProcessMonthTitleType = (monthTitle: any, year: string | number) => string;

// props

export default /*@__PURE__*/_defineComponent({
  __name: 'MonthCalendar',
  props: {
    organizationId: {},
    month: {},
    year: {},
    activeClass: {},
    prefixClass: {},
    activeDates: { default: () => [] },
    processMonthTitle: { type: Function, default: (monthTitle: any) => String(monthTitle) },
    lang: { default: 'ja' }
  },
  emits: ["submit", "toggleDate", "monthClickEvent"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

// emits
const emits = __emit;

//変数
const state: IMonthCalendar = reactive<IMonthCalendar>({
  showDays: [],
  isMouseDown: false,
}) as IMonthCalendar;

//computed
const weekTitleFontSizeAdjustLang = computed(() => {
  const fontSizeMapping = {
    ja: '16px',
    tw: '16px',
    en: '14px',
    pt: '14px',
    de: '14px',
    es: '14px',
    pl: '12px',
    ru: '14px',
  };
  return fontSizeMapping[props.lang];
});
const monthTitle = computed(() => {
  const monthMapping = {
    ja: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'],
    tw: ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月'],
    en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    pt: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
    de: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
    es: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
    pl: [
      'Styczeń',
      'Luty',
      'Marzec',
      'Kwiecień',
      'Maj',
      'Czerwiec',
      'Lipiec',
      'Sierpień',
      'Wrzesień',
      'Październik',
      'Listopad',
      'Grudzień',
    ],
    ru: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
  };
  return monthMapping[props.lang][(Number(props.month) as number) - 1];
});
const displayMonthTitle = computed(() => {
  return props.processMonthTitle(monthTitle.value, props.year);
});

// watch
watch(
  () => props.year,
  () => {
    // yearRef.value = props.year;
    initCalendar();
  }
);

watch(
  () => props.activeDates,
  () => {
    // activeDatesRef.value = props.activeDates;
    initCalendar();
  }
);

// イベント
onMounted(() => {
  initCalendar();
});

//関数
const initCalendar = () => {
  if (!props.year || !props.month) return [];
  const activeMonth = dayjs()
    .set('date', 1)
    .set('year', Number(props.year) as number)
    .set('month', (Number(props.month) as number) - 1);
  let firstDay: number = activeMonth.startOf('month').day() - 1;
  if (firstDay < 0) firstDay += 7;
  const lastDate: number = activeMonth.endOf('month').date();
  const weekRow: number = firstDay >= 5 ? 6 : 5;
  const WEEK = 7;
  let day = 0;
  const fullCol = Array.from(Array(weekRow * WEEK).keys()).map((i) => {
    const value: string | number = firstDay <= i ? (day++ % lastDate) + 1 : '';
    return {
      value,
      active: false,
      isOtherMonth: firstDay > i || day > lastDate,
    };
  });
  state.showDays = fullCol;

  // 把 toggleDate 的內容合併在 initCalendar 裡。
  props.activeDates.forEach((date) => {
    let oDate;

    if (typeof date === 'string') {
      oDate = {
        date: date,
        className: props.activeClass,
      };
    } else if (typeof date === 'object') {
      oDate = date;
    }

    const dayjsObj: dayjs.Dayjs = dayjs(oDate.date);
    if (dayjsObj.year() !== props.year) return;
    const activeDate: number = dayjsObj.date();
    const row: number = Math.floor(activeDate / 7);
    const activeArrayKey: number = (activeDate % 7) - 1 + firstDay + 7 * row;
    state.showDays[activeArrayKey].active = true; // to array index
    state.showDays[activeArrayKey].className = oDate.className;
  });
};
const showDayTitle = (day: number): string => {
  const dayMapping: { [key: string]: string[] } = {
    ja: ['月', '火', '水', '木', '金', '土', '日'],
    tw: ['一', '二', '三', '四', '五', '六', '日'],
    en: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
    pt: ['2ª', '3ª', '4ª', '5ª', '6ª', 'Sa', 'Do'],
    de: ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'],
    es: ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'],
    pl: ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Nie'],
    ru: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
  };
  return dayMapping[props.lang][day];
};

const toggleDay = (dayObj: any) => {
  // if (dayObj.isOtherMonth) return
  emits('toggleDate', {
    year: props.year,
    month: props.month,
    date: dayObj.value,
    selected: !dayObj.active,
    className: props.activeClass,
    organizationId: props.organizationId,
  });
};

const dragDay = (dayObj: any): void => {
  if (state.isMouseDown) toggleDay(dayObj);
};

const mouseDown = (dayObj: any): void => {
  toggleDay(dayObj);
  state.isMouseDown = true;
};

const mouseUp = () => {
  state.isMouseDown = false;
};

const classList = (dayObj: any) => {
  const oClassList = {
    'calendar__day--otherMonth': dayObj.isOtherMonth,
    [props.prefixClass]: dayObj.active,
  };
  // console.log('classList', dayObj, this.prefixClass)
  if (dayObj.active) oClassList[dayObj.className] = true;
  else oClassList[dayObj.className] = false;
  return oClassList;
};

const monthClickEvent = (year: string | number): void => {
  const monthYearInfo = {
    monthTitle: monthTitle,
    month: props.month,
    year: year,
  };
  emits('monthClickEvent', monthYearInfo);
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: "calendar",
      onMouseup: mouseUp,
      onMouseleave: _withModifiers(mouseUp, ["stop"])
    }, [
      _createElementVNode("div", {
        class: "calendar__title",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (monthClickEvent(_ctx.year)))
      }, _toDisplayString(displayMonthTitle.value), 1),
      _createElementVNode("div", _hoisted_2, [
        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(7, (day, key) => {
          return _createElementVNode("div", {
            key: `title${day}`,
            class: "calendar__day day__weektitle",
            style: _normalizeStyle({ fontSize: weekTitleFontSizeAdjustLang.value })
          }, _toDisplayString(showDayTitle(key)), 5)
        }), 64)),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(state.showDays, (dayObj, key) => {
          return (_openBlock(), _createElementBlock("div", {
            key: `day${key}`,
            class: "calendar__day"
          }, [
            _createElementVNode("div", {
              class: _normalizeClass(["day", classList(dayObj)]),
              onMouseover: ($event: any) => (dragDay(dayObj)),
              onMousedown: ($event: any) => (mouseDown(dayObj))
            }, _toDisplayString(dayObj.value), 43, _hoisted_3)
          ]))
        }), 128))
      ])
    ], 32)
  ]))
}
}

})